import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2, AlertCircle, Loader2, MessageCircle, Lock } from 'lucide-react';
import { useLang } from '../contexts/LanguageContext';
type FormData = {
  name: string;
  phone: string;
  email: string;
  business: string;
  type: string;
  city: string;
  hasWebsite: string;
  message: string;
};
type Status = 'idle' | 'sending' | 'success' | 'error';
type Props = {
  /** Prefix for input ids so the form can appear twice on the page */
  idPrefix: string;
  /** Hide optional fields for a shorter form (used in the popup) */
  compact?: boolean;
  /** Called when the user wants to leave the success screen */
  onDone?: () => void;
};
const initialData: FormData = {
  name: '',
  phone: '',
  email: '',
  business: '',
  type: '',
  city: '',
  hasWebsite: '',
  message: ''
};

// Formats US numbers as (847) 282-2098 while typing; leaves others untouched
function formatPhone(value: string) {
  if (/[^\d\s()+-]/.test(value) || value.trim().startsWith('+')) return value;
  const d = value.replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
const FORM_ENDPOINT = 'https://formsubmit.co/ajax/aselitecontractor@gmail.com';
// Leads from this form go by text message (SMS) to +1 847-282-2098
const SMS_NUMBER = '+18472822098';
export function LeadForm({
  idPrefix,
  compact = false,
  onDone
}: Props) {
  const {
    t
  } = useLang();
  const c = t.contact;
  const [data, setData] = useState<FormData>(initialData);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [status, setStatus] = useState<Status>('idle');
  // The compact (popup) form is split in 2 short steps to reduce friction
  const [step, setStep] = useState<1 | 2>(1);
  const businessTypes = [...t.socialProof.types, c.typeOther];
  const fid = (field: keyof FormData) => `${idPrefix}-${field}`;
  const update = (field: keyof FormData, value: string) => {
    const next = field === 'phone' ? formatPhone(value) : value;
    setData(d => ({
      ...d,
      [field]: next
    }));
    if (errors[field]) setErrors(e => ({
      ...e,
      [field]: undefined
    }));
  };
  const validate = (only?: (keyof FormData)[]) => {
    const check = (f: keyof FormData) => !only || only.includes(f);
    const next: Partial<Record<keyof FormData, string>> = {};
    if (check('name') && !data.name.trim()) next.name = c.required;
    if (check('phone')) {
      if (!data.phone.trim()) next.phone = c.required;else if (data.phone.replace(/\D/g, '').length < 10) next.phone = c.invalidPhone;
    }
    if (check('email') && data.email.trim() && !/^\S+@\S+\.\S+$/.test(data.email)) next.email = c.invalidEmail;
    if (check('business') && !data.business.trim()) next.business = c.required;
    if (check('type') && !data.type) next.type = c.required;
    setErrors(next);
    return Object.keys(next).length === 0;
  };
  const smsUrl = () => {
    const lines = [c.waIntro, '', `${c.name}: ${data.name}`, `${c.phone}: ${data.phone}`, `${c.business}: ${data.business}`, `${c.type}: ${data.type}`, data.city ? `${c.city}: ${data.city}` : '', data.email ? `Email: ${data.email}` : '', data.hasWebsite ? `${c.hasWebsite} ${data.hasWebsite}` : '', data.message ? `${c.message}: ${data.message}` : ''].filter((line, i) => line !== '' || i === 1);
    // "?&body=" works on both iPhone and Android
    return `sms:${SMS_NUMBER}?&body=${encodeURIComponent(lines.join('\n'))}`;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (compact && step === 1) {
      if (validate(['name', 'phone'])) setStep(2);
      return;
    }
    if (!validate()) return;
    // Open the phone's Messages app with the lead details pre-written
    window.location.href = smsUrl();
    setStatus('sending');
    try {
      const res = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          _subject: `Nuevo lead: ${data.business} (${data.type})`,
          _template: 'table',
          _captcha: 'false',
          Nombre: data.name,
          Telefono: data.phone,
          Email: data.email || '—',
          Negocio: data.business,
          Tipo: data.type,
          Ciudad: data.city || '—',
          'Tiene website': data.hasWebsite || '—',
          Mensaje: data.message || '—',
          Origen: compact ? 'Popup' : 'Formulario de contacto'
        })
      });
      if (!res.ok) throw new Error('Request failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };
  const inputBase = 'w-full bg-dark border rounded-xl px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gold/40 focus:border-gold transition-colors';
  const inputClass = (field: keyof FormData) => `${inputBase} ${errors[field] ? 'border-red-500/70' : 'border-white/10'}`;
  const labelClass = 'block text-sm font-medium text-gray-300 mb-2';
  const fieldError = (field: keyof FormData) => errors[field] ? <p id={`${fid(field)}-error`} className="mt-1.5 text-xs text-red-400" data-id="element-203" data-mp="3kija48xt1owl">
        {errors[field]}
      </p> : null;
  const a11y = (field: keyof FormData) => ({
    id: fid(field),
    'aria-invalid': !!errors[field],
    'aria-describedby': errors[field] ? `${fid(field)}-error` : undefined
  });
  return <AnimatePresence mode="wait" data-id="element-204" data-mp="2wlvmu09kficz">
      {status === 'success' ? <motion.div key="success" initial={{
      opacity: 0,
      scale: 0.96
    }} animate={{
      opacity: 1,
      scale: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease: [0.23, 1, 0.32, 1]
    }} className={`text-center ${compact ? 'py-6' : 'py-10'}`} role="status" data-id="element-205" data-mp="31k6woazv1sv7">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/15 text-gold mb-6" data-id="element-206" data-mp="xf86569c41eo">
            <CheckCircle2 className="w-8 h-8" data-id="element-207" data-mp="tb0jrfqcmxjm" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-3" data-id="element-208" data-mp="ocp7kycl0ay8">
            {c.successTitle}
          </h3>
          <p className="text-gray-400 max-w-md mx-auto mb-8" data-id="element-209" data-mp="jf7ac57xfk62">{c.successText}</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center" data-id="element-210" data-mp="1h1weakm6w6uk">
            <a href={smsUrl()} className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold hover:bg-gold-400 text-dark font-bold rounded-xl transition-colors" data-id="element-211" data-mp="1h3z3x3jfjxyr">
              <MessageCircle className="w-5 h-5" data-id="element-212" data-mp="12djhjlfa2jz0" />
              {c.successWhatsapp}
            </a>
            <button type="button" onClick={() => {
          setData(initialData);
          setStatus('idle');
          setStep(1);
          onDone?.();
        }} className="px-6 py-3 text-gray-400 hover:text-white font-medium rounded-xl border border-white/10 hover:border-white/20 transition-colors" data-id="element-213" data-mp="1zsdwhv9ebk1o">
              {onDone ? t.legal.close : c.sendAnother}
            </button>
          </div>
        </motion.div> : <motion.form key="form" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} onSubmit={handleSubmit} noValidate className={compact ? 'space-y-4' : 'space-y-5'} data-id="element-214" data-mp="2havxiapaol9x">
          {compact && <div data-id="element-215" data-mp="2v29dxfrwp30q">
              <div className="flex gap-1.5 mb-3" role="progressbar" aria-valuemin={1} aria-valuemax={2} aria-valuenow={step} aria-label={`${step} / 2`} data-id="element-216" data-mp="2v3u7b22hifx0">
                {[1, 2].map(s => <span key={s} className={`h-1 flex-1 rounded-full transition-colors ${s <= step ? 'bg-gold' : 'bg-white/10'}`} data-id="element-217" data-mp="38zfv55wzetnu" />)}
              </div>
              <p className="text-white font-bold" data-id="element-218" data-mp="83xtogom1sjk">
                {step === 1 ? c.step1Title : c.step2Title}
              </p>
            </div>}

          {(!compact || step === 1) && <div className={`grid gap-4 sm:gap-5 ${compact ? '' : 'sm:grid-cols-2'}`} data-id="element-219" data-mp="3bmr8ewt4knfb">
            <div data-id="element-220" data-mp="36ohcgjzktisn">
              <label htmlFor={fid('name')} className={labelClass} data-id="element-221" data-mp="36ktixku4swdx">
                {c.name} <span className="text-gold" data-id="element-222" data-mp="3lddeo0eyc1lc">*</span>
              </label>
              <input {...a11y('name')} type="text" autoComplete="name" autoFocus={compact} value={data.name} onChange={e => update('name', e.target.value)} placeholder={c.namePh} className={inputClass('name')} data-id="element-223" data-mp="3gkss9jydkqru" />
              {fieldError('name')}
            </div>
            <div data-id="element-224" data-mp="1864bf55rwzbp">
              <label htmlFor={fid('phone')} className={labelClass} data-id="element-225" data-mp="186noa7gkgxiv">
                {c.phone} <span className="text-gold" data-id="element-226" data-mp="ueplsfhd1zvo">*</span>
              </label>
              <input {...a11y('phone')} type="tel" inputMode="tel" autoComplete="tel" value={data.phone} onChange={e => update('phone', e.target.value)} placeholder={c.phonePh} className={inputClass('phone')} data-id="element-227" data-mp="yerlij3jcj8b" />
              {fieldError('phone')}
            </div>
          </div>}

          {compact && step === 2 && <div className="space-y-4" data-id="element-228" data-mp="1p0g3otphbnq">
              <div data-id="element-229" data-mp="1nz3hfx1kh3w">
                <label htmlFor={fid('business')} className={labelClass} data-id="element-230" data-mp="1nfmq2j4nyrg">
                  {c.business} <span className="text-gold" data-id="element-231" data-mp="6ov7rulb259u">*</span>
                </label>
                <input {...a11y('business')} type="text" autoComplete="organization" autoFocus value={data.business} onChange={e => update('business', e.target.value)} placeholder={c.businessPh} className={inputClass('business')} data-id="element-232" data-mp="1kuae9mko831l" />
                {fieldError('business')}
              </div>
              <fieldset aria-describedby={errors.type ? `${fid('type')}-error` : undefined} data-id="element-233" data-mp="118nvzithqonh">
                <legend className={labelClass} data-id="element-234" data-mp="163jmgej2x3yh">
                  {c.type} <span className="text-gold" data-id="element-235" data-mp="s6mug9r85gg0">*</span>
                </legend>
                <div className="flex flex-wrap gap-2" data-id="element-236" data-mp="x4ojdbbc09ze">
                  {businessTypes.map(type => <button key={type} type="button" onClick={() => update('type', type)} aria-pressed={data.type === type} className={`px-3.5 py-2 rounded-full border text-sm font-medium transition-colors ${data.type === type ? 'bg-gold text-dark border-gold' : 'bg-dark text-gray-300 border-white/10 hover:border-white/25'}`} data-id="element-237" data-mp="2mjcul986rlvu">
                      {type}
                    </button>)}
                </div>
                {fieldError('type')}
              </fieldset>
            </div>}

          {!compact && <div className="grid sm:grid-cols-2 gap-4 sm:gap-5" data-id="element-238" data-mp="3te3jd8keeweq">
            <div data-id="element-239" data-mp="3tdk2zwaecg2t">
              <label htmlFor={fid('business')} className={labelClass} data-id="element-240" data-mp="3tbzfurqevzez">
                {c.business} <span className="text-gold" data-id="element-241" data-mp="3pd9v2e31nflb">*</span>
              </label>
              <input {...a11y('business')} type="text" autoComplete="organization" value={data.business} onChange={e => update('business', e.target.value)} placeholder={c.businessPh} className={inputClass('business')} data-id="element-242" data-mp="1z13x4wlij3yh" />
              {fieldError('business')}
            </div>
            <div data-id="element-243" data-mp="7v7rdrirf9ug">
              <label htmlFor={fid('type')} className={labelClass} data-id="element-244" data-mp="7upm65e4dlae">
                {c.type} <span className="text-gold" data-id="element-245" data-mp="cw5b48jf4by2">*</span>
              </label>
              <select {...a11y('type')} value={data.type} onChange={e => update('type', e.target.value)} className={`${inputClass('type')} ${data.type ? '' : 'text-gray-600'}`} data-id="element-246" data-mp="gx8ngl29bpuj">
                <option value="" disabled data-id="element-247" data-mp="qolejag8ebzi">
                  {c.typePh}
                </option>
                {businessTypes.map(type => <option key={type} value={type} className="text-white" data-id="element-248" data-mp="10lhegrs2dpsg">
                    {type}
                  </option>)}
              </select>
              {fieldError('type')}
            </div>
          </div>}

          {!compact && <>
              <div className="grid sm:grid-cols-2 gap-5" data-id="element-249" data-mp="2l27dloovffaj">
                <div data-id="element-250" data-mp="2l4tnhuz8i6vx">
                  <label htmlFor={fid('city')} className={labelClass} data-id="element-251" data-mp="2g3m9vfaeudhc">
                    {c.city}
                  </label>
                  <input {...a11y('city')} type="text" autoComplete="address-level2" value={data.city} onChange={e => update('city', e.target.value)} placeholder={c.cityPh} className={inputClass('city')} data-id="element-252" data-mp="2b5cjrj3jm32l" />
                </div>
                <div data-id="element-253" data-mp="2u3x6z02bcoay">
                  <label htmlFor={fid('email')} className={labelClass} data-id="element-254" data-mp="2u2dl8txw1l1s">
                    {c.email}
                  </label>
                  <input {...a11y('email')} type="email" autoComplete="email" value={data.email} onChange={e => update('email', e.target.value)} placeholder={c.emailPh} className={inputClass('email')} data-id="element-255" data-mp="3rk402xnezjvj" />
                  {fieldError('email')}
                </div>
              </div>

              <fieldset data-id="element-256" data-mp="32agen8cu5qxf">
                <legend className={labelClass} data-id="element-257" data-mp="32c1d3929tu0k">{c.hasWebsite}</legend>
                <div className="flex gap-3" data-id="element-258" data-mp="3q00zjwsjuy9c">
                  {[c.yes, c.no].map(option => <button key={option} type="button" onClick={() => update('hasWebsite', option)} aria-pressed={data.hasWebsite === option} className={`flex-1 sm:flex-none sm:w-28 py-2.5 rounded-xl border font-medium transition-colors ${data.hasWebsite === option ? 'bg-gold text-dark border-gold' : 'bg-dark text-gray-300 border-white/10 hover:border-white/25'}`} data-id="element-259" data-mp="3v05hzsd0q327">
                      {option}
                    </button>)}
                </div>
              </fieldset>

              <div data-id="element-260" data-mp="2b2ewbrovh3wf">
                <label htmlFor={fid('message')} className={labelClass} data-id="element-261" data-mp="2b2ybj0n51e6r">
                  {c.message}
                </label>
                <textarea {...a11y('message')} rows={4} value={data.message} onChange={e => update('message', e.target.value)} placeholder={c.messagePh} className={`${inputClass('message')} resize-none`} data-id="element-262" data-mp="2748tgur5n8gs" />
              </div>
            </>}

          {status === 'error' && <div role="alert" className="flex items-start gap-3 bg-red-500/10 border border-red-500/30 rounded-xl p-4" data-id="element-263" data-mp="33rvkgd5glymu">
              <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" data-id="element-264" data-mp="2yssqpouxjeu6" />
              <div className="text-sm" data-id="element-265" data-mp="1weutte9uw80j">
                <p className="text-white font-bold" data-id="element-266" data-mp="1whz50vlqo40p">{c.errorTitle}</p>
                <p className="text-gray-400" data-id="element-267" data-mp="21d65lujposle">
                  {c.errorText}{' '}
                  <a href={smsUrl()} className="text-gold font-bold hover:underline" data-id="element-268" data-mp="25hm9jq9rlpxz">
                    847-282-2098 →
                  </a>
                </p>
              </div>
            </div>}

          <button type="submit" disabled={status === 'sending'} className="w-full inline-flex items-center justify-center gap-2 py-4 bg-gold hover:bg-gold-400 disabled:opacity-70 disabled:cursor-not-allowed text-dark font-bold rounded-xl transition-colors" data-id="element-269" data-mp="1hq8gtj7pbsch">
            {status === 'sending' ? <>
                <Loader2 className="w-5 h-5 animate-spin" data-id="element-270" data-mp="tz30z41rh026" />
                {c.sending}
              </> : compact && step === 1 ? c.next : c.submit}
          </button>

          {compact && step === 2 && status !== 'sending' && <button type="button" onClick={() => setStep(1)} className="w-full text-center text-sm text-gray-400 hover:text-white transition-colors" data-id="element-271" data-mp="mftrlat991d4">
              {c.back}
            </button>}

          <p className="flex items-center justify-center gap-2 text-xs text-gray-500" data-id="element-272" data-mp="8ind3sfkdotp">
            <Lock className="w-3.5 h-3.5" data-id="element-273" data-mp="3q3xyr18alms" />
            {c.privacy}
          </p>
        </motion.form>}
    </AnimatePresence>;
}